import { useEffect, useState } from 'react'
import { useTranslation } from 'react-i18next'
import { Video } from 'lucide-react'
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog'
import { Button } from '@/components/ui/button'
import { Input } from '@/components/ui/input'
import { Label } from '@/components/ui/label'
import { Switch } from '@/components/ui/switch'
import { TaskAssigneeSelect } from '@/components/task-assignee-select'
import { orpc } from '@/lib/orpc-client'
import { useAuthState } from '@/lib/auth-state'
import { useLocale } from '@/i18n/locale'
import { MeetingLanguageToggle } from './meeting-language-toggle'
import { formatSelectionRange, toLocalInputValue, type GridSelection } from './week-grid-selection'

/**
 * The meeting the user just painted in the week grid (PLAN-085 §1.3).
 *
 * "HUB video meeting" is on by default: that mints a room code, seeds the
 * notes document and mails everyone an `.ics`. Turning it off falls back to
 * the plain calendar entry the calendar has always had, with an optional
 * external Meet/Teams link.
 */

export interface CreateMeetingResult {
  id: string
  roomCode: string | null
  kind: 'hub' | 'external'
  inviteFailures: string[]
}

export function CreateMeetingDialog({
  projectId,
  selection,
  day,
  open,
  onOpenChange,
  onCreated,
}: {
  projectId: string
  selection: GridSelection | null
  day: Date | null
  open: boolean
  onOpenChange: (open: boolean) => void
  onCreated: (result: CreateMeetingResult) => void
}) {
  const { t } = useTranslation('meetings')
  const { locale } = useLocale()
  const { me } = useAuthState()
  const selfId = me?.user.id

  const [title, setTitle] = useState('')
  const [startAt, setStartAt] = useState('')
  const [endAt, setEndAt] = useState('')
  const [invitees, setInvitees] = useState<string[]>([])
  const [language, setLanguage] = useState<'sv' | 'en'>(locale === 'en' ? 'en' : 'sv')
  const [isHub, setIsHub] = useState(true)
  const [meetingUrl, setMeetingUrl] = useState('')
  const [saving, setSaving] = useState(false)
  const [error, setError] = useState<string | null>(null)

  // Re-seed the form each time a fresh selection opens the dialog.
  useEffect(() => {
    if (!open || !selection || !day) return
    const start = new Date(day)
    start.setHours(0, 0, 0, 0)
    start.setMinutes(selection.startMinutes)
    const end = new Date(day)
    end.setHours(0, 0, 0, 0)
    end.setMinutes(selection.endMinutes)
    setTitle('')
    setStartAt(toLocalInputValue(start))
    setEndAt(toLocalInputValue(end))
    setInvitees(selfId ? [selfId] : [])
    setLanguage(locale === 'en' ? 'en' : 'sv')
    setIsHub(true)
    setMeetingUrl('')
    setError(null)
  }, [open, selection, day, selfId, locale])

  const submit = async () => {
    if (!startAt || !endAt) return
    setSaving(true)
    setError(null)
    try {
      const created = await orpc.meetings.create({
        projectId,
        title: title.trim() || t('create.defaultTitle'),
        startAt: new Date(startAt).toISOString(),
        endAt: new Date(endAt).toISOString(),
        ...(isHub
          ? { kind: 'hub' as const, invitees, language }
          : meetingUrl.trim()
            ? { meetingUrl: meetingUrl.trim() }
            : {}),
      })
      onCreated({
        id: created.id,
        roomCode: created.roomCode,
        kind: created.kind,
        inviteFailures: created.inviteFailures,
      })
      onOpenChange(false)
    } catch {
      setError(t('create.error'))
    } finally {
      setSaving(false)
    }
  }

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="sm:max-w-md">
        <DialogHeader>
          <DialogTitle>{t('create.title')}</DialogTitle>
          <DialogDescription>{selection ? formatSelectionRange(selection) : ''}</DialogDescription>
        </DialogHeader>

        <div className="space-y-4">
          <div className="space-y-1.5">
            <Label htmlFor="meeting-title">{t('create.name')}</Label>
            <Input
              id="meeting-title"
              autoFocus
              value={title}
              placeholder={t('create.namePlaceholder')}
              onChange={(e) => setTitle(e.target.value)}
            />
          </div>

          <div className="grid grid-cols-2 gap-3">
            <div className="space-y-1.5">
              <Label htmlFor="meeting-start">{t('create.start')}</Label>
              <Input
                id="meeting-start"
                type="datetime-local"
                value={startAt}
                onChange={(e) => setStartAt(e.target.value)}
              />
            </div>
            <div className="space-y-1.5">
              <Label htmlFor="meeting-end">{t('create.end')}</Label>
              <Input
                id="meeting-end"
                type="datetime-local"
                value={endAt}
                onChange={(e) => setEndAt(e.target.value)}
              />
            </div>
          </div>

          <div className="flex items-start justify-between gap-4 rounded-lg border p-3">
            <div className="space-y-0.5">
              <p className="flex items-center gap-1.5 text-sm font-medium">
                <Video className="h-3.5 w-3.5" />
                {t('create.hubMeeting')}
              </p>
              <p className="text-muted-foreground text-xs">{t('create.hubMeetingHint')}</p>
            </div>
            <Switch
              checked={isHub}
              aria-label={t('create.hubMeeting')}
              onCheckedChange={setIsHub}
            />
          </div>

          {isHub ? (
            <>
              <div className="space-y-1.5">
                <Label>{t('create.invitees')}</Label>
                <TaskAssigneeSelect
                  projectId={projectId}
                  selectedIds={invitees}
                  onChange={setInvitees}
                  lockedIds={selfId ? [selfId] : []}
                  placeholder={t('create.invitees')}
                />
                <p className="text-muted-foreground text-xs">{t('create.inviteesHint')}</p>
              </div>

              <div className="space-y-1.5">
                <Label>{t('create.language')}</Label>
                <MeetingLanguageToggle value={language} onChange={setLanguage} />
              </div>
            </>
          ) : (
            <div className="space-y-1.5">
              <Label htmlFor="meeting-url">{t('create.externalUrl')}</Label>
              <Input
                id="meeting-url"
                value={meetingUrl}
                placeholder={t('create.externalUrlPlaceholder')}
                onChange={(e) => setMeetingUrl(e.target.value)}
              />
            </div>
          )}

          {error && <p className="text-destructive text-sm">{error}</p>}
        </div>

        <DialogFooter>
          <Button variant="ghost" onClick={() => onOpenChange(false)} disabled={saving}>
            {t('create.cancel')}
          </Button>
          <Button onClick={() => void submit()} disabled={saving || !startAt || !endAt}>
            {saving ? t('create.submitting') : t('create.submit')}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  )
}
